/**
 * usePermissions hook for NeoTIM
 * Combines store + parser + capabilities for easy permission checking
 *
 * IMPORTANT: Capabilities are UI-only.
 * - Use can() / canAny() / canAll() for authorization decisions
 * - Use capabilities.has() only for deciding which components to MOUNT
 * - Never use capabilities in route beforeLoad guards - use requirePermission() instead
 */

import { matchesPermission } from "../permissions/parser";
import type { CapabilityId, PermissionContext, UserType } from "../permissions/types";
import { usePermissionStore } from "../stores/permissions.store";

// ─── Pure capability derivation (moved from legacy capabilities.ts) ──────────

/**
 * Derive capabilities from permission strings and user context
 */
function deriveCapabilities(
  permissions: readonly string[],
  context: PermissionContext,
): Set<CapabilityId> {
  const caps = new Set<CapabilityId>();

  const hasPermissionMatching = (pattern: (perm: string) => boolean): boolean =>
    Array.from(permissions).some(pattern);

  const hasPermissionStartingWith = (prefix: string): boolean =>
    hasPermissionMatching((p) => p.startsWith(prefix));

  // admin: has wildcard permission
  if (hasPermissionMatching((p) => p === "*")) {
    caps.add("admin");
  }

  // hr_global: has rh write permissions with filiale:own but no scope:managed
  // Single-pass per permission: the SAME grant must be rh.* AND carry
  // `:filiale:own`. Two independent predicates over the whole set would
  // wrongly combine e.g. `ventes.x:filiale:own` with `rh.conges.view`.
  const hasRhFilialeOwn = hasPermissionMatching(
    (p) => p.startsWith("rh.") && p.includes(":filiale:own") && !p.includes(":scope:"),
  );
  if (hasRhFilialeOwn) {
    caps.add("hr_global");
  }

  // hr_dept: has rh validate with owned scope AND is dept head
  const hasHrDeptPermission = hasPermissionMatching(
    (p) => p.startsWith("rh.") && p.includes(":filiale:own") && p.includes(":scope:managed"),
  );
  if (hasHrDeptPermission && context.is_dept_head) {
    caps.add("hr_dept");
  }

  // hr_team: has rh validate with owned scope, NOT dept head, IS manager
  if (hasHrDeptPermission && !context.is_dept_head && context.is_manager) {
    caps.add("hr_team");
  }

  // hr_self: has self-scoped permissions but not global/dept/team
  const hasHrSelf = hasPermissionMatching((p) => p.includes(":scope:self"));
  if (hasHrSelf && !caps.has("hr_global") && !caps.has("hr_dept") && !caps.has("hr_team")) {
    caps.add("hr_self");
  }

  // hr_multifiliale: has rh permissions with :filiale:* (all filiales)
  const hasHrMultifiliale = hasPermissionMatching(
    (p) => p.startsWith("rh.") && p.includes(":filiale:*"),
  );
  if (hasHrMultifiliale) {
    caps.add("hr_multifiliale");
  }

  // audit: has view-only access to rh, no create/edit/delete/validate
  // Same single-pass binding as hasRhFilialeOwn: the `:filiale:own` predicate
  // must apply to the SAME rh.* permission, or `ventes.x:filiale:own` would
  // combine with any rh.* grant into a false "view-only RH" audit role.
  const hasRhViewOwn = hasPermissionMatching(
    (p) => p.startsWith("rh.") && p.includes(":filiale:own"),
  );
  const hasRhWrite = hasPermissionMatching(
    (p) =>
      p.startsWith("rh.") &&
      (p.includes(".create") ||
        p.includes(".edit") ||
        p.includes(".delete") ||
        p.includes(".validate")),
  );
  if (hasRhViewOwn && !hasRhWrite) {
    caps.add("audit");
  }

  // security_admin: has security module permissions
  if (hasPermissionStartingWith("security.")) {
    caps.add("security_admin");
  }

  return caps;
}

/**
 * Determine user type from capabilities
 */
function determineUserType(capabilities: Set<CapabilityId>): UserType {
  if (capabilities.has("admin")) {
    return "admin";
  }

  if (capabilities.has("hr_global") || capabilities.has("hr_multifiliale")) {
    return "hr-global";
  }

  if (capabilities.has("hr_dept") || capabilities.has("hr_team")) {
    return "manager";
  }

  return "employee";
}

/**
 * Hook return type
 */
export interface UsePermissionsReturn {
  /** Check if user has a specific permission */
  can: (required: string) => boolean;
  /** Check if user has any of the specified permissions */
  canAny: (required: string[]) => boolean;
  /** Check if user has all of the specified permissions */
  canAll: (required: string[]) => boolean;
  /** Whether the user has a wildcard permission (*) */
  hasWildcard: boolean;
  /** Derived capabilities (UI-only) */
  capabilities: Set<CapabilityId>;
  /** User type classification */
  userType: UserType;
  /** User's permission context */
  context: PermissionContext;
}

/**
 * Hook for permission checking
 *
 * @example
 * const { can, canAny, capabilities, userType } = usePermissions()
 *
 * // Authorization (use in component rendering)
 * if (can('rh.conges.view')) {
 *   return <CongesList />
 * }
 *
 * // UI capability (use for mounting entire sections)
 * if (capabilities.has('hr_global')) {
 *   return <DRHDashboard />
 * }
 */
export function usePermissions(): UsePermissionsReturn {
  const permissions = usePermissionStore((state) => state.permissions);
  const hasWildcard = usePermissionStore((state) => state.hasWildcard);
  const context = usePermissionStore((state) => state.context);

  /**
   * Check if user has a specific permission
   */
  const can = (required: string): boolean => {
    if (hasWildcard) return true;
    return Array.from(permissions).some((grant) => matchesPermission(grant, required, context));
  };

  /**
   * Check if user has any of the specified permissions
   */
  const canAny = (required: string[]): boolean => required.some(can);

  /**
   * Check if user has all of the specified permissions
   */
  const canAll = (required: string[]): boolean => required.every(can);

  /**
   * Derive capabilities from permissions
   * NOTE: This is computed on every render. For performance-critical
   * cases, consider memoizing with useMemo.
   */
  const capabilities = deriveCapabilities(permissions, context);

  /**
   * Determine user type from capabilities
   */
  const userType = determineUserType(capabilities);

  return {
    can,
    canAny,
    canAll,
    hasWildcard,
    capabilities,
    userType,
    context,
  };
}

/**
 * Selector for checking if user is authenticated with permissions
 */
export function useIsAuthenticated(): boolean {
  const permissions = usePermissionStore((state) => state.permissions);
  const hasWildcard = usePermissionStore((state) => state.hasWildcard);
  return permissions.length > 0 || hasWildcard;
}

/**
 * Selector for getting capabilities only (for performance)
 */
export function useCapabilities(): Set<CapabilityId> {
  const permissions = usePermissionStore((state) => state.permissions);
  const context = usePermissionStore((state) => state.context);
  return deriveCapabilities(permissions, context);
}

/**
 * Selector for getting user type only (for performance)
 */
export function useUserType(): UserType {
  const capabilities = useCapabilities();
  return determineUserType(capabilities);
}

// Re-export as default for backward compatibility
export default usePermissions;
